// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useState, type KeyboardEvent } from 'react';

import { useCanEdit } from '../../../app/CaseRoleContext';
import { lengthInput, parseLength } from '../../../bygning/geometriEdit';
import { levelText, type StoreyRow } from '../../../bygning/storeyEdit';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { Kbd } from '../../Kbd';
import styles from './StoreyPanel.module.css';

export interface StoreyActions {
  /** Show storey `id` on the plan (the row opens). */
  onActive(id: number): void;
  onRename(guid: string, name: string): void;
  /** World heights; the editor carries walls, rooms and floor slabs along when `carry` is on. */
  onHeights(guid: string, heights: { zMin?: number; zMax?: number }): void;
  onGround(guid: string, on: boolean): void;
  onSplit(guid: string, z: number, name: string): void;
  onMerge(guid: string, other: string): void;
  /** Resolves to the server's refusal in Danish, or null when it was deleted. */
  onDelete(guid: string): Promise<string | null>;
  /** Everything on `guid` to storey `toId`, then delete `guid`. */
  onMoveAllAndDelete(guid: string, toId: number): void;
  /** The selection to storey `toId`. */
  onMoveSelected(toId: number): void;
  onAdd(): void;
  onCarry(on: boolean): void;
  /** Snit across the storey, under the plan. */
  onSection(): void;
  /** Genberegn for etagen: the solids re-derived (the whole building until reusex-7f3j). */
  onRederive(): void;
  onClose(): void;
}

export interface StoreyPanelProps {
  rows: StoreyRow[];
  /** The storey the plan shows (its row is open). */
  activeId: number | null;
  /** Elements selected (Flyt valgte hertil). */
  selected: number;
  carry: boolean;
  rederiving: boolean;
  actions: StoreyActions;
}

/** A height typed as a level ("0,00", "-0,30", "2,8"): committed on Enter or blur, Esc puts it back. */
function LevelInput({ label, value, canEdit, onCommit }: { label: string; value: number; canEdit: boolean; onCommit: (v: number) => void }) {
  const shown = lengthInput(value);
  const [text, setText] = useState(shown);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setText(shown);
    setBad(false);
  }, [shown]);
  const commit = () => {
    if (text === shown) return;
    const v = parseLength(text);
    if (v === null) return setBad(true);
    onCommit(v);
  };
  return (
    <label className={styles.level}>
      <span className={styles.levelLabel}>{label}</span>
      <span className={styles.inputWrap}>
        <input
          className={bad ? `${styles.input} ${styles.bad}` : styles.input}
          value={text}
          inputMode="decimal"
          aria-label={label}
          aria-invalid={bad || undefined}
          readOnly={!canEdit}
          onChange={(e) => {
            setText(e.target.value);
            setBad(false);
          }}
          onBlur={commit}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setText(shown);
              setBad(false);
            }
          }}
        />
        <span className={styles.unit}>m</span>
      </span>
    </label>
  );
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('da-DK')} ${n === 1 ? one : many}`;

/** "12 vægge · 4 åbninger · 5 rum · 2 dæk", or "Tom". */
function countsText(c: StoreyRow['counts']): string {
  const parts = [
    c.walls ? plural(c.walls, 'væg', 'vægge') : null,
    c.openings ? plural(c.openings, 'åbning', 'åbninger') : null,
    c.spaces ? plural(c.spaces, 'rum', 'rum') : null,
    c.slabs ? plural(c.slabs, 'dæk', 'dæk') : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Tom';
}

/**
 * Etager (frontend-u0o.7; user 2026-11-04: "a way to define/change our
 * floors if that fails automatically"): every storey, top first, with its
 * level label (S for the ground storey), its floor and ceiling and what
 * stands on it. The open row (the storey the plan shows) edits: name,
 * floor and ceiling (typed here, or dragged in Snit over the cloud's
 * height histogram), Stueetage, Del ved højde, Flet med etagen under,
 * Slet (refused by the server while elements remain — then Flyt alt til …
 * og slet), Flyt valgte hertil. Below: Tilføj etage øverst, whether the
 * heights carry the walls, rooms and floor slabs along, Vis i snit and
 * Genberegn.
 */
export function StoreyPanel({ rows, activeId, selected, carry, rederiving, actions }: StoreyPanelProps) {
  const canEdit = useCanEdit();
  const [splitFor, setSplitFor] = useState<string | null>(null);
  const [splitZ, setSplitZ] = useState('');
  const [splitName, setSplitName] = useState('');
  const [refusal, setRefusal] = useState<{ guid: string; text: string; to: number | null } | null>(null);
  const [name, setName] = useState('');
  const active = rows.find((r) => r.id === activeId) ?? null;
  useEffect(() => setName(active?.name ?? ''), [active?.guid, active?.name]);
  useEffect(() => {
    setSplitFor(null);
    setRefusal((r) => (r && r.guid === active?.guid ? r : null));
  }, [active?.guid]);

  const startSplit = (r: StoreyRow) => {
    setSplitFor(r.guid);
    setSplitZ(lengthInput((r.zMin + r.zMax) / 2));
    setSplitName('');
  };
  const doSplit = (r: StoreyRow) => {
    const z = parseLength(splitZ);
    if (z === null) return;
    actions.onSplit(r.guid, z, splitName);
    setSplitFor(null);
  };
  const doDelete = (r: StoreyRow) => {
    void actions.onDelete(r.guid).then((text) => {
      if (text === null) return setRefusal(null);
      // The storey under it takes them by default, else the one over it.
      const at = rows.findIndex((x) => x.guid === r.guid);
      const other = rows[at + 1] ?? rows[at - 1] ?? null;
      setRefusal({ guid: r.guid, text, to: other?.id ?? null });
    });
  };

  return (
    <section className={styles.panel} aria-labelledby="geo-storeys-title">
      <div className={styles.head}>
        <p className={styles.eyebrow}>Bygning</p>
        <button type="button" className={styles.close} onClick={actions.onClose} aria-label="Luk Etager" title="Luk (E)">
          ×
        </button>
      </div>
      <h2 id="geo-storeys-title" className={styles.title}>
        Etager
      </h2>
      <p className={styles.lead}>Ret etagerne, hvis detektionen ramte forkert. Træk gulve og lofter i snittet, eller skriv koterne.</p>

      <ol className={styles.rows}>
        {rows.map((r, i) => {
          const open = r.id === activeId;
          const below = rows[i + 1];
          return (
            <li key={r.guid} className={open ? `${styles.row} ${styles.rowOpen}` : styles.row}>
              <button type="button" className={styles.rowHead} aria-expanded={open} onClick={() => actions.onActive(r.id)}>
                <span className={r.isGround || r.label === 'S' ? `${styles.badge} ${styles.badgeGround}` : styles.badge} title={r.isGround ? 'Stueetage (sat)' : r.label === 'S' ? 'Stueetage (gættet ud fra koten)' : undefined}>
                  {r.label}
                </span>
                <span className={styles.rowName}>{r.name}</span>
                <span className={styles.rowLevels}>
                  {levelText(r.zMin)} – {levelText(r.zMax)}
                </span>
              </button>
              <p className={styles.counts}>
                {countsText(r.counts)}
                {r.edited && <span className={styles.edited}> · redigeret</span>}
              </p>
              {open && (
                <div className={styles.body}>
                  <label className={styles.field}>
                    <span className={styles.levelLabel}>Navn</span>
                    <input
                      className={styles.input}
                      value={name}
                      readOnly={!canEdit}
                      aria-label="Etagens navn"
                      onChange={(e) => setName(e.target.value)}
                      onBlur={() => name.trim() && name !== r.name && actions.onRename(r.guid, name)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (name.trim() && name !== r.name) actions.onRename(r.guid, name);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          setName(r.name);
                        }
                      }}
                    />
                  </label>
                  <div className={styles.levels}>
                    <LevelInput label="Gulv" value={r.zMin} canEdit={canEdit} onCommit={(v) => actions.onHeights(r.guid, { zMin: v })} />
                    <LevelInput label="Loft" value={r.zMax} canEdit={canEdit} onCommit={(v) => actions.onHeights(r.guid, { zMax: v })} />
                    <span className={styles.height}>
                      Højde <b>{r.height.toFixed(2).replace('.', ',')} m</b>
                    </span>
                  </div>
                  {canEdit && (
                    <Checkbox
                      label="Stueetage (bygningens indgang, S)"
                      checked={r.isGround}
                      onChange={(e) => actions.onGround(r.guid, e.target.checked)}
                    />
                  )}
                  {canEdit && splitFor !== r.guid && (
                    <div className={styles.actions}>
                      <Button size="sm" variant="outline" onClick={() => startSplit(r)} title="Del etagen i to ved en kote">
                        Del ved højde…
                      </Button>
                      <Button size="sm" variant="outline" disabled={!below} onClick={() => below && actions.onMerge(r.guid, below.guid)} title={below ? `Flet ${r.name} og ${below.name} til én etage` : 'Ingen etage under'}>
                        Flet med etagen under
                      </Button>
                      <Button size="sm" variant="outline" tone="danger" onClick={() => doDelete(r)}>
                        Slet
                      </Button>
                    </div>
                  )}
                  {canEdit && splitFor === r.guid && (
                    <div className={styles.split}>
                      <LevelInputText label="Del ved" value={splitZ} onChange={setSplitZ} onEnter={() => doSplit(r)} />
                      <label className={styles.field}>
                        <span className={styles.levelLabel}>Ny etage</span>
                        <input className={styles.input} value={splitName} placeholder={`${r.name} (2)`} aria-label="Den nye etages navn" onChange={(e) => setSplitName(e.target.value)} />
                      </label>
                      <p className={styles.note}>Det, der står over koten, flytter med til den nye etage.</p>
                      <div className={styles.actions}>
                        <Button size="sm" variant="primary" onClick={() => doSplit(r)}>
                          Del
                        </Button>
                        <Button size="sm" variant="filled" onClick={() => setSplitFor(null)}>
                          Annullér
                        </Button>
                      </div>
                    </div>
                  )}
                  {refusal?.guid === r.guid && (
                    <div className={styles.refusal} role="alert">
                      <p>{refusal.text}</p>
                      {rows.length > 1 && (
                        <div className={styles.actions}>
                          <label className={styles.moveTo}>
                            Flyt alt til
                            <select value={refusal.to ?? ''} onChange={(e) => setRefusal({ ...refusal, to: Number(e.target.value) })} aria-label="Flyt elementerne til etage">
                              {rows
                                .filter((x) => x.guid !== r.guid)
                                .map((x) => (
                                  <option key={x.guid} value={x.id}>
                                    {x.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={refusal.to === null}
                            onClick={() => {
                              if (refusal.to !== null) actions.onMoveAllAndDelete(r.guid, refusal.to);
                              setRefusal(null);
                            }}
                          >
                            Flyt og slet
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {canEdit && selected > 0 && (
                    <Button size="sm" variant="filled" onClick={() => actions.onMoveSelected(r.id)} title="Flyt de valgte vægge, rum og dæk til denne etage (de beholder deres placering)">
                      Flyt {selected === 1 ? '1 valgt' : `${selected} valgte`} hertil
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {rows.length === 0 && <p className={styles.note}>Ingen etager endnu. Kør Etager i pipelinen, eller tilføj en.</p>}

      <div className={styles.footer}>
        {canEdit && (
          <Button size="sm" variant="filled" onClick={actions.onAdd} title="En ny etage oven på den øverste (etager imellem: Del ved højde)">
            + Tilføj etage øverst
          </Button>
        )}
        {canEdit && <Checkbox label="Flyt vægge, rum og dæk med højderne" checked={carry} onChange={(e) => actions.onCarry(e.target.checked)} />}
        <div className={styles.actions}>
          <Button size="sm" variant="outline" onClick={actions.onSection} title="Snit gennem etagen med punktskyens højder (SN)">
            Vis i snit <Kbd>SN</Kbd>
          </Button>
          {canEdit && (
            <Button size="sm" variant="outline" disabled={rederiving} onClick={actions.onRederive}>
              {rederiving ? 'Genberegner…' : 'Genberegn for etagen'}
            </Button>
          )}
        </div>
        {canEdit && <p className={styles.note}>Genberegning kører for hele bygningen (dæk og tag) — kun den ene etage kommer senere.</p>}
      </div>
    </section>
  );
}

/** A level as text (the split's height before it is sent). */
function LevelInputText({ label, value, onChange, onEnter }: { label: string; value: string; onChange: (v: string) => void; onEnter: () => void }) {
  const bad = value.trim() !== '' && parseLength(value) === null;
  return (
    <label className={styles.level}>
      <span className={styles.levelLabel}>{label}</span>
      <span className={styles.inputWrap}>
        <input
          className={bad ? `${styles.input} ${styles.bad}` : styles.input}
          value={value}
          inputMode="decimal"
          aria-label={label}
          aria-invalid={bad || undefined}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onEnter();
            }
          }}
        />
        <span className={styles.unit}>m</span>
      </span>
    </label>
  );
}
